/**
 * Emotion-matrix ENGINE — PRESENTATION LAYER ONLY.
 *
 * This module powers the Brief screen's "emotion spider" (a hexagon radar of how
 * an edit is likely to LAND emotionally, per crowd). It is deliberately decoupled
 * from `@desk/core` scoring: nothing here ever feeds `scoreEdit`, `resolveEdit`,
 * or any pack validation. It is a simple, deterministic sketch used to explain
 * intent to the player, not a scoring signal.
 *
 * DATA-DRIVEN: the engine holds NO scenario-specific data. Every scenario ships a
 * `LandingConfig` (see `content/landing/*`) that carries its crowds, raw-footage
 * baseline, and the tunable edit-choice -> emotion magnitudes ("levers"). The
 * engine reads that config, so new levels are authored in DATA, not code.
 *
 * Six axes, drawn clockwise from the top vertex of the hexagon. Ordered so the
 * three "dark" reads group on one side and the three "light" reads on the other:
 *   Rage · Alarm · Doubt · Funny · Pity · Warm
 *
 * Each crowd has two shaded zones:
 *   • GREEN  — what the crowd WANTS (land your reel here).
 *   • RED    — the crowd's BACKLASH (keep out of here).
 */

import type { ResolvedEdit } from "@desk/core";

/** The six presentation emotion axes, in draw order (top vertex, clockwise). */
export const EMOTION_AXES = ["Rage", "Alarm", "Doubt", "Funny", "Pity", "Warm"] as const;
export type EmotionAxis = (typeof EMOTION_AXES)[number];

/**
 * Per-axis colour palette (presentation only) so the spider axis labels and any
 * inline mention of an axis word read in the SAME colour across the UI:
 *   Rage = pink-red · Alarm = amber · Doubt = cool/purple · Pity = blue ·
 *   Warm = warm-orange · Funny = yellow. Kept consistent with the Caption tool's
 *   impact-chip tones. Never feeds scoring.
 */
export const EMOTION_AXIS_COLORS: Record<EmotionAxis, string> = {
  Rage: "#ff3d7f",
  Alarm: "#ffc857",
  Doubt: "#c79bff",
  Funny: "#ffe14d",
  Pity: "#7fb4ff",
  Warm: "#ff8a4d",
};

/** Colour used for a cred/receipts DOWN move (a ding), matching the cred convention. */
export const CRED_DOWN_COLOR = "#ff3d7f";

/** A 0..1 value per axis. */
export type EmotionVector = Record<EmotionAxis, number>;

/**
 * Human-scale intensity buckets used to author crowd profiles. Exported so
 * per-scenario `LandingConfig` modules can spec crowds with the SAME buckets.
 */
export const EMOTION_LEVELS = { none: 0, low: 0.18, med: 0.5, high: 0.85 } as const;
export type EmotionLevel = keyof typeof EMOTION_LEVELS;

export const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

const EMPTY_VECTOR: EmotionVector = { Rage: 0, Alarm: 0, Doubt: 0, Funny: 0, Pity: 0, Warm: 0 };

/** A fresh zero vector (all six axes at 0). Presentation only. */
export function zeroVector(): EmotionVector {
  return { ...EMPTY_VECTOR };
}

/**
 * Build a full vector from a sparse `{ axis: level }` spec (missing axes -> 0).
 * Authoring helper for `LandingConfig` crowd profiles; presentation only.
 */
export function emotionVec(spec: Partial<Record<EmotionAxis, EmotionLevel>>): EmotionVector {
  const out = { ...EMPTY_VECTOR };
  for (const axis of EMOTION_AXES) {
    const level = spec[axis];
    if (level) out[axis] = EMOTION_LEVELS[level];
  }
  return out;
}

export interface CrowdEmotionProfile {
  /** Stable key used to look a crowd's profile up from the Brief. */
  id: string;
  label: string;
  /** What the crowd wants — land your reel inside this (teal) zone. */
  green: EmotionVector;
  /** The crowd's backlash — keep out of this (pink/red) zone. */
  red: EmotionVector;
}

/** Ordered axis values for a vector (matches EMOTION_AXES / radar draw order). */
export function toAxisValues(v: EmotionVector): number[] {
  return EMOTION_AXES.map((a) => clamp01(v[a]));
}

// ── LandingConfig: the per-scenario level-design struct ───────────────────────
//
// Everything a scenario needs to drive "How it's landing" lives here as DATA so a
// designer can author a new level without touching the engine. Vet Day's config
// (content/levels/vet-day/level.json, loaded via content/levels/index.ts) carries
// the EXACT values this engine used to hard-code, so behaviour is identical.

/** A caption STYLE (documentary/urgent/kinetic/clean) -> emotion contribution. */
export type CaptionStyleLevers = Record<string, Partial<EmotionVector>>;

/**
 * Where the draggable caption is snapped on the video — a light gameplay lever.
 *   • `chyron`  — lower-third bar; reads like a news lower-third (looks official).
 *   • `subject` — centered over the subject; COVERS context (reads as hiding).
 *   • `top`     — headline banner; neutral / punchy default.
 * Presentation only; never feeds scoring.
 */
export type CaptionPlacementZone = "top" | "chyron" | "subject";

/** Caption PLACEMENT zone -> small emotion contribution (a light lever). */
export type CaptionPlacementLevers = Partial<Record<CaptionPlacementZone, Partial<EmotionVector>>>;

/** An emoji reaction-tag bucket: any matched glyph maps to `axis`. */
export interface EmojiBucket {
  axis: EmotionAxis;
  glyphs: string[];
}

/**
 * A keyword-matched lever (sounds / looks). `roles` matches the resolved op's
 * role; `match` matches substrings of the asset/treatment id. First matching
 * lever in the ordered list wins (mirrors the original if/else-if chain).
 */
export interface KeywordLever {
  id: string;
  /** Op roles this lever matches (e.g. sting/ambience). Optional. */
  roles?: string[];
  /** Substrings of the asset/treatment id this lever matches. */
  match: string[];
  emotion: Partial<EmotionVector>;
}

/**
 * A per-level, per-BEAT STICKER ZONE (finer than a Focus subject — e.g. "the
 * cat's eyes" vs "the cat" — AND scoped to a moment in the story, not the
 * whole reel). Zones are purely GEOMETRY + TIMING; they do nothing on their
 * own. A zone only produces an emotion when it is paired with a specific
 * sticker id by an authored `StickerCombo` (see below) AND the placed
 * sticker's appear-window overlaps the zone's beat. On-video the zone is
 * shown ONLY while the playhead is inside its beat (see `CreativeOverlay`) —
 * a marker only, never a hint about which sticker it wants. Landing on a
 * zone with no combo for the placed sticker, or landing outside the zone's
 * beat window, is purely cosmetic.
 */
export interface StickerZone {
  /** Stable zone id (e.g. "zone-cat-eyes"), referenced by `StickerCombo.zoneId`. */
  id: string;
  /** Player-facing label shown on the zone hint (e.g. "Cat's Eyes"). */
  label: string;
  /** Normalized box, top-left origin (same convention as `SubjectBox.rect`). */
  rect: { x: number; y: number; w: number; h: number };
  /** Story beat this zone belongs to (authored; e.g. "beat-wait"). */
  beatId: string;
  /** Resolved (by the level loader) from `beatId`'s pack beat — source-tick range start. */
  startTick: number;
  /** Resolved (by the level loader) from `beatId`'s pack beat — source-tick range end. */
  endTick: number;
}

/**
 * The AUTHORED shape of a `StickerZone` as it lives in `level.json` — geometry
 * + label + `beatId`, but no resolved `startTick`/`endTick` (the level loader
 * attaches those from the pack's beat once it has loaded `pack.beats`).
 */
export type AuthoredStickerZone = Omit<StickerZone, "startTick" | "endTick">;

/**
 * A HAND-CRAFTED (specific sticker × specific zone) pairing that produces an
 * emotion. This is the ONLY thing that makes a sticker placement do anything
 * — everything else (any other sticker, any other zone, any placement off
 * every zone) is purely cosmetic. Replaces the old blanket "any sticker on a
 * subject box" model: landing now rewards a DELIBERATE, authored placement,
 * not just placement-in-general.
 */
export interface StickerCombo {
  /** Catalog sticker id this combo requires (e.g. "sticker-sunglasses"). */
  stickerId: string;
  /** `StickerZone.id` this combo requires the sticker to land inside. */
  zoneId: string;
  /** Emotion contributed when this exact sticker lands in this exact zone. */
  emotion: Partial<EmotionVector>;
}

/**
 * The combo (if any) a sticker placement triggers: `stickerId` must match a
 * combo's `stickerId` AND the anchor (milli, 0-1000, same units as
 * `StickerInstance.anchorXMilli/YMilli`) must fall inside that combo's zone
 * AND the sticker's appear-window `[stickerStartTick, stickerEndTick)` must
 * OVERLAP the zone's beat window `[zone.startTick, zone.endTick)` (overlap =
 * `stickerStartTick < zone.endTick && stickerEndTick > zone.startTick`) — the
 * sticker has to be ON the zone DURING the zone's beat, not just ever. When
 * several of the sticker's (spatially + temporally) matching zones contain
 * the anchor, the SMALLEST-area zone wins (most specific authored intent). No
 * match (wrong sticker, wrong zone, off every zone, or on the right
 * zone/sticker but outside its beat) is a cosmetic placement -> `null`.
 * Shared by the landing engine, the impact chips, and the sim so all three
 * agree on what counts as "on combo". Presentation only; never feeds scoring.
 */
export function stickerComboForPlacement(
  stickerId: string,
  anchorXMilli: number,
  anchorYMilli: number,
  stickerStartTick: number,
  stickerEndTick: number,
  zones: readonly StickerZone[],
  combos: readonly StickerCombo[],
): StickerCombo | null {
  const ax = anchorXMilli / 1000;
  const ay = anchorYMilli / 1000;
  let best: StickerCombo | null = null;
  let bestArea = Infinity;
  for (const combo of combos) {
    if (combo.stickerId !== stickerId) continue;
    const zone = zones.find((z) => z.id === combo.zoneId);
    if (!zone) continue;
    const { x, y, w, h } = zone.rect;
    if (ax < x || ax > x + w || ay < y || ay > y + h) continue;
    if (!(stickerStartTick < zone.endTick && stickerEndTick > zone.startTick)) continue;
    const area = w * h;
    if (area < bestArea) {
      bestArea = area;
      best = combo;
    }
  }
  return best;
}

/** Look / filter / effect levers. */
export interface LookLevers {
  /**
   * Exact treatment-id -> emotion (per-effect magnitude), checked FIRST. Lets a
   * designer say "Noir filter -> Doubt 0.1"; combos are additive because the
   * engine bumps each contribution. Empty for Vet Day.
   */
  byId: Record<string, Partial<EmotionVector>>;
  /** Keyword groups (first match wins) for generic warm/cool grades. */
  groups: KeywordLever[];
}

/** One axis contribution scaled by a magnitude, saturating at a cap. */
export interface ScaledContribution {
  axis: EmotionAxis;
  /** Per-unit magnitude. */
  per: number;
  /** Saturation cap for this axis's contribution. */
  cap: number;
}

/** Cut / omission lever: losing reassuring context reads "abandoned". */
export interface ContextCutLever {
  /** Context beat id -> how much reassurance it carries. */
  beatWeights: Record<string, number>;
  /** Weight multiplier when a context beat is fully omitted. */
  omitFactor: number;
  /** Weight multiplier when a context beat is trimmed (partial loss). */
  trimFactor: number;
  /** Per-axis contribution scaled by total context lost. */
  contributions: ScaledContribution[];
}

/** Focus / crop lever: a tight crop that isolates the subject reads distorted. */
export interface FocusCropLever {
  /** A crop with width OR height <= this (milli) counts as "tight". */
  tightThreshold: number;
  /** Entity ids that CARRY context — a tight crop centred here does not distort. */
  carrierEntityIds: string[];
  /** Per-axis contribution scaled by how many frames isolate the subject. */
  contributions: ScaledContribution[];
}

/** Jump-cut "looks edited" Doubt tuning. */
export interface JumpCutLever {
  /** Doubt added per visible jump-cut without the transitions addon. */
  perJump: number;
  /** Reduced per-jump Doubt once Seamless Transitions is owned. */
  perJumpSeamless: number;
  /** Saturation cap — a few jumps is already "looks edited". */
  cap: number;
}

/** "Poor editing" leak Doubt tuning. */
export interface LeakLever {
  /** Doubt added per second of leak on a kept, untrimmed leaky beat. */
  perSecond: number;
  /** Saturation cap — a loose cut looks sloppy without pinning the axis. */
  cap: number;
}

/**
 * A kept beat's CLEAN content end vs its COARSE authored boundary, in source
 * ticks (game-side presentation metadata; NOT part of `@desk/core` or the pack
 * schema). When `coarseEndTick > cleanEndTick`, keeping the beat to its coarse
 * end LEAKS the start of the next action — a sloppy cut. Trimming to `cleanEndTick`
 * (Precision Trim) removes that leak. Ticks are at the pack's tick rate.
 */
export interface BeatCleanEnd {
  /** The authored / coarse beat end (where the clip currently stops). */
  coarseEndTick: number;
  /** Where the clean content actually ends (trim target). */
  cleanEndTick: number;
}

/** The tunable edit-choice -> emotion mappings for one scenario. */
export interface LandingLevers {
  /** Caption STYLE -> emotion. */
  captionStyles: CaptionStyleLevers;
  /**
   * Caption PLACEMENT zone -> small emotion contribution (a LIGHT gameplay
   * lever). Snapping the caption to the chyron reads official and slightly LOWERS
   * Doubt; parking it over the subject COVERS context and slightly RAISES Doubt;
   * the top banner is a neutral default. Magnitudes are intentionally small.
   * Optional — scenarios without placement feedback omit it. Presentation only;
   * never feeds scoring.
   */
  captionPlacement?: CaptionPlacementLevers;
  /** Emoji reaction-tag buckets (caption tokens / stickers heuristics). */
  emojiBuckets: EmojiBucket[];
  /** Emotion added when a caption token matches an emoji bucket. */
  emojiTagMagnitude: number;
  /** Headline framing token -> emotion (deniable spin vs checkable claim). */
  framingTokens: Record<string, Partial<EmotionVector>>;
  /** Sound stems (ordered; first match wins). */
  sounds: KeywordLever[];
  /**
   * Per-level STICKER ZONES (finer-grained than Focus subjects) a designer has
   * drawn for the sticker mechanic. Pure geometry + a label; do nothing on
   * their own (see `stickerCombos`). Default `[]` for a scenario that defines
   * none.
   */
  stickerZones: StickerZone[];
  /**
   * Per-level HAND-CRAFTED (sticker × zone) combos — the ONLY thing that makes
   * a sticker placement contribute an emotion. Every other placement (right
   * sticker/wrong zone, wrong sticker/right zone, off every zone) is purely
   * cosmetic. Default `[]` for a scenario that defines none.
   */
  stickerCombos: StickerCombo[];
  /** Look / filter (single color grade) levers. */
  looks: LookLevers;
  /**
   * Stackable EFFECT -> emotion (per-effect magnitude), keyed by effect id
   * (e.g. `look-rain`). Additive across active effects, so combos sum. An absent
   * or empty entry means the effect is purely COSMETIC (zero emotion), which is a
   * valid, intentional design choice. Optional — scenarios without effect feedback
   * omit it entirely. Presentation only; never feeds scoring.
   */
  effects?: Record<string, Partial<EmotionVector>>;
  /**
   * CAMERA-MOTION preset -> emotion (per-preset magnitude), keyed by the motion
   * preset id (e.g. `motion-push-in`). A camera move is now a presentation-only
   * TIMED EFFECT (placed per beat in the Look tool); it no longer feeds the core
   * score, so its read lands here instead. Additive across the beats that carry a
   * move. An absent or empty entry (e.g. `motion-steady`) is purely COSMETIC. Read
   * off `resolved.motions` (the move still rides the scored graph so it also drives
   * the camera-move preview). Optional. Presentation only; never feeds scoring.
   */
  motions?: Record<string, Partial<EmotionVector>>;
  /** Cut / omission context-loss lever. */
  contextCut: ContextCutLever;
  /** Focus / crop isolation lever. */
  focusCrop: FocusCropLever;
  /** Jump-cut Doubt tuning. */
  jumpCut: JumpCutLever;
  /** Leak Doubt tuning. */
  leak: LeakLever;
  /** Per-beat clean ends for leak detection. */
  beatCleanEnds: Record<string, BeatCleanEnd>;
}

/** A complete per-scenario landing configuration (a level-design struct). */
export interface LandingConfig {
  /** SourcePack id this config drives. */
  packId: string;
  /** Emotion axes (defaults to the six EMOTION_AXES). */
  axes: readonly EmotionAxis[];
  /** Crowds for this scenario, keyed by id (green wants + red backlash). */
  crowds: Record<string, CrowdEmotionProfile>;
  /** Iteration order (served crowd first), for the compact reaction rows. */
  crowdOrder: string[];
  /** The crowd you are cutting for at this level. */
  servedCrowdId: string;
  /** Raw-footage baseline emotion (before any edit) for this scenario. */
  baseVector: EmotionVector;
  /** Tunable edit-choice -> emotion mappings. */
  levers: LandingLevers;
}

/** Default jump-cut tuning (fallback when a config's lever is not supplied). */
export const DEFAULT_JUMP_CUT: JumpCutLever = { perJump: 0.12, perJumpSeamless: 0.05, cap: 0.42 };
/** Default leak tuning (fallback when a config's lever is not supplied). */
export const DEFAULT_LEAK: LeakLever = { perSecond: 0.16, cap: 0.3 };

const has = (hay: string, needle: string) => hay.includes(needle);

/**
 * The emotion a HEADLINE FRAMING contributes, summed over the caption tokens it
 * emits (per the config's `framingTokens`). This is the SAME contribution
 * `editEmotionVector` folds into the landing, exposed so the Caption tool can
 * show each framing's actual impact truthfully. Unknown tokens contribute
 * nothing. Presentation only; never feeds scoring.
 */
export function framingEmotionContribution(
  tokenIds: readonly string[],
  config: LandingConfig,
): EmotionVector {
  const out: EmotionVector = { ...EMPTY_VECTOR };
  const framingTokens = config.levers.framingTokens;
  for (const tokenId of tokenIds) {
    const spec = framingTokens[tokenId];
    if (!spec) continue;
    for (const axis of EMOTION_AXES) {
      const amount = spec[axis];
      if (amount) out[axis] = clamp01(out[axis] + amount);
    }
  }
  return out;
}

/** The raw-footage baseline vector for a scenario (a fresh copy). */
export function baseEmotionVector(config: LandingConfig): EmotionVector {
  return { ...config.baseVector };
}

/** The clean-end map for a scenario. Presentation only. */
export function beatCleanEnds(config: LandingConfig): Record<string, BeatCleanEnd> {
  return config.levers.beatCleanEnds;
}

/** Minimal shape of a resolved cut this module reads (keep/omit/trim + in/out). */
interface CutLike {
  mode?: string;
  inTick?: number;
  outTick?: number;
}

/**
 * True when the resolved edit RETAINS at least one beat to play. A beat is kept
 * unless it is omitted or trimmed to an empty span (out <= in), mirroring the
 * "retained" rule scoring uses. An empty reel (every beat omitted / nothing to
 * play) has NO kept beats, which the emotion vector treats as a hard zero (no
 * raw-footage baseline, no contributions). Presentation only; never feeds scoring.
 */
export function hasKeptBeats(resolved: ResolvedEdit | null | undefined): boolean {
  if (!resolved) return false;
  const cuts = resolved.cuts as Map<string, CutLike> | undefined;
  for (const beatId of resolved.beatOrder ?? []) {
    const cut = cuts?.get?.(beatId);
    if (cut?.mode === "omit") continue;
    if (cut?.mode === "trim") {
      if (cut.inTick === undefined || cut.outTick === undefined) continue;
      if (cut.outTick <= cut.inTick) continue;
    }
    return true;
  }
  return false;
}

/**
 * The leak (in source ticks) of a single beat given its resolved cut and its
 * clean end. A beat that is omitted, absent from the clean-end map, or already
 * trimmed to at/under its clean end has no leak. When the beat is precision-
 * trimmed (session state), the caller passes `trimmed=true` and the leak is 0.
 * Presentation only.
 */
export function beatLeakTicks(
  beatId: string,
  cut: CutLike | undefined,
  cleanEnds: Record<string, BeatCleanEnd>,
  trimmed = false,
): number {
  const ce = cleanEnds[beatId];
  if (!ce) return 0;
  if (trimmed) return 0;
  if (cut?.mode === "omit") return 0;
  // A real trim op that already ends at/under the clean end leaves no leak.
  const effectiveEnd =
    cut?.mode === "trim" && cut.outTick !== undefined ? cut.outTick : ce.coarseEndTick;
  return Math.max(0, effectiveEnd - ce.cleanEndTick);
}

/**
 * Total leak (source ticks) across all kept, untrimmed leaky beats in an edit.
 * `precisionTrimmedBeatIds` is the session set of beats the player has trimmed to
 * their clean end (Precision Trim); those contribute no leak. Presentation only.
 */
export function editLeakTicks(
  resolved: ResolvedEdit | null | undefined,
  config: LandingConfig,
  precisionTrimmedBeatIds?: readonly string[] | ReadonlySet<string>,
): number {
  const cleanEnds = config.levers.beatCleanEnds;
  const trimmed =
    precisionTrimmedBeatIds instanceof Set
      ? precisionTrimmedBeatIds
      : new Set(precisionTrimmedBeatIds ?? []);
  let total = 0;
  for (const beatId of Object.keys(cleanEnds)) {
    const cut = (resolved?.cuts as Map<string, CutLike> | undefined)?.get?.(beatId);
    total += beatLeakTicks(beatId, cut, cleanEnds, trimmed.has(beatId));
  }
  return total;
}

/**
 * Doubt added by a given leak duration (source ticks -> seconds via `tickRate`).
 * Zero when there is no leak; scales with leak seconds and saturates at the cap.
 * Presentation only; never feeds scoring.
 */
export function leakDoubt(leakTicks: number, tickRate = 30, lever: LeakLever = DEFAULT_LEAK): number {
  if (leakTicks <= 0 || tickRate <= 0) return 0;
  const seconds = leakTicks / tickRate;
  return Math.min(lever.cap, seconds * lever.perSecond);
}

/**
 * The Doubt a given number of jump-cuts adds (presentation only). Owning the
 * Seamless Transitions addon lowers the per-jump penalty, so this returns a
 * strictly smaller number for the same jump count when owned. Never feeds scoring.
 */
export function jumpCutDoubt(
  jumpCuts: number,
  seamlessTransitions = false,
  lever: JumpCutLever = DEFAULT_JUMP_CUT,
): number {
  if (jumpCuts <= 0) return 0;
  const per = seamlessTransitions ? lever.perJumpSeamless : lever.perJump;
  return Math.min(lever.cap, per * jumpCuts);
}

/** Presentation knobs for the raw-footage baseline + jump-cut / leak Doubt. */
export interface EditEmotionOptions {
  /** Raw-footage baseline to start from (e.g. `baseEmotionVector(config)`). */
  base?: EmotionVector;
  /** Number of visible jump-cuts in the edited timeline. */
  jumpCuts?: number;
  /** True when the player owns the Seamless Transitions editing addon. */
  seamlessTransitions?: boolean;
  /**
   * Pack tick rate, for converting a beat leak (ticks) to seconds. Supplying a
   * tickRate ALSO opts this call into "poor editing" leak Doubt (the editor's
   * live panel). Quick reads (Brief / Compare / Publish) omit it, so their
   * landing is unaffected by leak — matching the original packId-gated behaviour.
   */
  tickRate?: number;
  /** Session set of beats trimmed to their clean end (Precision Trim). */
  precisionTrimmedBeatIds?: readonly string[] | ReadonlySet<string>;
  /**
   * Active stackable EFFECT ids (rain / lightning / focus blur). These live in
   * game-side presentation state, NOT the scored graph, so they are supplied here
   * rather than read off `resolved`. Each contributes per the config's
   * `levers.effects` map (additive); unknown or cosmetic effects contribute
   * nothing. Presentation only; never feeds scoring.
   */
  activeEffectIds?: readonly string[];
  /**
   * The zone the draggable caption is currently snapped to (session/UI state, so
   * it arrives here rather than off `resolved`). Folds the config's
   * `levers.captionPlacement[zone]` contribution into the landing — a light,
   * data-driven lever. Only applies when the reel actually carries a caption.
   * Undefined = no placement contribution. Presentation only; never feeds scoring.
   */
  captionPlacement?: CaptionPlacementZone;
}

/**
 * Map a resolved edit to a 0..1 emotion vector over the six axes, driven by the
 * scenario's `LandingConfig`.
 *
 * Deterministic and intentionally simple; this NEVER feeds scoring. Every
 * edit-choice -> emotion magnitude comes from `config.levers`, so retuning a
 * scenario (or authoring a new one) is pure DATA.
 *
 * Pass `opts.base` (e.g. `baseEmotionVector(config)`) to start from the raw
 * footage's inherent emotion; edits then move the vector from there. With no
 * base the mapping starts at zero. Presentation only; never feeds scoring.
 */
export function editEmotionVector(
  resolved: ResolvedEdit | null | undefined,
  config: LandingConfig,
  opts?: EditEmotionOptions,
): EmotionVector {
  const levers = config.levers;
  const out: EmotionVector = opts?.base ? { ...opts.base } : { ...EMPTY_VECTOR };
  if (!resolved) return out;
  // Empty reel (all beats omitted / nothing to play): with no kept content the
  // reel reads as a hard ZERO. The raw-footage baseline and every edit
  // contribution only apply once at least one beat survives. Presentation only.
  if (!hasKeptBeats(resolved)) return { ...EMPTY_VECTOR };
  const bump = (axis: EmotionAxis, amount: number) => {
    out[axis] = clamp01(out[axis] + amount);
  };
  const applySpec = (spec: Partial<EmotionVector> | undefined) => {
    if (!spec) return;
    for (const axis of EMOTION_AXES) {
      const amount = spec[axis];
      if (amount) bump(axis, amount);
    }
  };

  // — Captions: claim intent + emoji tags —
  for (const cap of resolved.captions) {
    const style = cap.style ?? "documentary";
    applySpec(levers.captionStyles[style]);
    const tokenText = [...cap.tokenIds, ...(cap.emphasizedTokenIds ?? [])].join(" ").toLowerCase();
    for (const { axis, glyphs } of levers.emojiBuckets) {
      if (glyphs.some((g) => has(tokenText, g))) bump(axis, levers.emojiTagMagnitude);
    }
    // Headline framing: each preset spins the SAME footage differently, so each
    // moves the landing differently. Applied per emitted caption token.
    for (const tokenId of cap.tokenIds) applySpec(levers.framingTokens[tokenId]);
  }

  // Caption PLACEMENT (a LIGHT gameplay lever): where the player snaps the
  // draggable caption on the video nudges the landing per the config's
  // `captionPlacement` map. Chyron reads official (Doubt down); over the subject
  // covers context (Doubt up); the top banner is neutral. This is session/UI
  // state, so it arrives via `opts`, not `resolved`, and only applies when the
  // reel actually carries a caption. Presentation only; never feeds scoring.
  if (opts?.captionPlacement && levers.captionPlacement && resolved.captions.length > 0) {
    applySpec(levers.captionPlacement[opts.captionPlacement]);
  }

  // — Sound bed / sting: ominous vs upbeat vs warm (first matching lever wins) —
  for (const s of resolved.sounds) {
    const id = String(s.assetId).toLowerCase();
    for (const lever of levers.sounds) {
      const roleHit = s.role !== undefined && (lever.roles?.includes(String(s.role)) ?? false);
      const keyHit = lever.match.some((k) => has(id, k));
      if (roleHit || keyHit) {
        applySpec(lever.emotion);
        break;
      }
    }
  }

  // — Stickers: contribute ONLY when the specific sticker lands in the
  //   specific zone an authored combo pairs it with, AND its appear-window
  //   overlaps that zone's beat; every other placement (wrong sticker, wrong
  //   zone, off every zone, or on-zone but outside the beat) is cosmetic. —
  for (const st of resolved.stickers) {
    if (!st.stickerId) continue;
    const combo = stickerComboForPlacement(
      st.stickerId,
      st.anchorXMilli,
      st.anchorYMilli,
      st.startTick,
      st.endTick,
      levers.stickerZones,
      levers.stickerCombos,
    );
    if (combo) applySpec(combo.emotion);
  }

  // — Look / grade / effect: exact per-effect magnitude first, then keyword group. —
  const lookId = String(resolved.look?.treatmentId ?? "").toLowerCase();
  if (lookId) {
    const byId = levers.looks.byId[lookId];
    if (byId) {
      applySpec(byId);
    } else {
      for (const lever of levers.looks.groups) {
        if (lever.match.some((k) => has(lookId, k))) {
          applySpec(lever.emotion);
          break;
        }
      }
    }
  }

  // Stackable EFFECTS (rain / lightning / focus blur): game-side presentation
  // state, so they arrive via opts, not `resolved`. Each active effect adds its
  // configured per-effect magnitude (additive for combos). Cosmetic effects (no
  // config entry) add nothing. Presentation only; never feeds scoring.
  const effectLevers = levers.effects;
  if (effectLevers && opts?.activeEffectIds) {
    for (const effectId of opts.activeEffectIds) {
      applySpec(effectLevers[effectId.toLowerCase()] ?? effectLevers[effectId]);
    }
  }

  // Camera MOTION (a presentation-only timed effect): each beat that carries a
  // non-neutral move nudges the landing per the config's `levers.motions` map.
  // Motion rides the scored graph (`resolved.motions`) because the same op drives
  // the camera-move preview, so — unlike effects — it reads off `resolved`, not
  // opts. A preset with no entry (e.g. steady) contributes nothing. Presentation
  // only; never feeds scoring.
  const motionLevers = levers.motions;
  if (motionLevers && resolved.motions) {
    for (const motionOp of resolved.motions.values()) {
      if (!motionOp.presetId) continue;
      applySpec(motionLevers[motionOp.presetId]);
    }
  }

  // Cutting / omission (Clip): removing the reassuring context beats makes the
  // reel read WORSE. Weighted by HOW MUCH context is cut (omit counts full, a
  // trim counts half). Presentation only.
  const cc = levers.contextCut;
  let contextCut = 0;
  for (const beatId of Object.keys(cc.beatWeights)) {
    const weight = cc.beatWeights[beatId]!;
    const mode = resolved.cuts?.get?.(beatId)?.mode;
    if (mode === "omit") contextCut += weight * cc.omitFactor;
    else if (mode === "trim") contextCut += weight * cc.trimFactor;
  }
  if (contextCut > 0) {
    for (const c of cc.contributions) bump(c.axis, Math.min(c.cap, c.per * contextCut));
  }

  // Framing / crop (Focus): a tight crop that ISOLATES the subject and cuts the
  // surrounding context reads as abandoned / mysterious. A tight crop that stays
  // on a context carrier preserves the story and does NOT distort. Scaled by how
  // many beats are isolated. Presentation only.
  const fc = levers.focusCrop;
  const carriers = new Set(fc.carrierEntityIds);
  let isolatingFrames = 0;
  for (const frame of resolved.frames.values()) {
    const w = frame.crop?.wMilli ?? 1000;
    const h = frame.crop?.hMilli ?? 1000;
    const tight = (w > 0 && w <= fc.tightThreshold) || (h > 0 && h <= fc.tightThreshold);
    if (!tight) continue;
    const subject = frame.subjectEntityId ?? null;
    if (subject && carriers.has(subject)) continue;
    isolatingFrames += 1;
  }
  if (isolatingFrames > 0) {
    for (const c of fc.contributions) bump(c.axis, Math.min(c.cap, c.per * isolatingFrames));
  }

  // Visible jump-cuts (Clip / reorder): each non-contiguous boundary between kept
  // beats makes the reel look "edited", so it adds Doubt, capped so a few jumps
  // already saturates. Seamless Transitions softens each jump. Presentation only.
  const jumpDoubt = jumpCutDoubt(opts?.jumpCuts ?? 0, opts?.seamlessTransitions ?? false, levers.jumpCut);
  if (jumpDoubt > 0) bump("Doubt", jumpDoubt);

  // "Poor editing" leak (Clip): a kept beat held past its clean content end LEAKS
  // the start of the next action, so the cut reads sloppy and adds Doubt, scaled
  // by how long the leak runs. Precision-trimming removes it. Only folded in when
  // a tickRate is supplied (the editor's live panel opts in); quick reads leave it
  // out. Presentation only.
  if (opts?.tickRate !== undefined) {
    const leak = editLeakTicks(resolved, config, opts.precisionTrimmedBeatIds);
    const lDoubt = leakDoubt(leak, opts.tickRate, levers.leak);
    if (lDoubt > 0) bump("Doubt", lDoubt);
  }

  return out;
}

// ── Live cause->effect readout (presentation reading; never feeds scoring) ────

/** One axis that moved between two emotion vectors. */
export interface EmotionAxisChange {
  axis: EmotionAxis;
  /** "up" when the axis rose, "down" when it fell. */
  dir: "up" | "down";
  /** Signed magnitude of the change (current - previous). */
  delta: number;
}

/** A change smaller than this is treated as noise and ignored. */
export const EMOTION_CHANGE_MIN = 0.03;

/**
 * The most-changed axes between two emotion vectors, largest move first. Filters
 * out sub-threshold noise and returns at most `limit` axes (default 2).
 * Presentation only; never feeds scoring.
 */
export function emotionVectorChanges(
  prev: EmotionVector,
  next: EmotionVector,
  limit = 2,
  min = EMOTION_CHANGE_MIN,
): EmotionAxisChange[] {
  const changes: EmotionAxisChange[] = [];
  for (const axis of EMOTION_AXES) {
    const delta = clamp01(next[axis]) - clamp01(prev[axis]);
    if (Math.abs(delta) < min) continue;
    changes.push({ axis, dir: delta > 0 ? "up" : "down", delta });
  }
  changes.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  return changes.slice(0, Math.max(0, limit));
}

/**
 * Short plain-words gloss for why an axis moving in a direction matters, e.g.
 * Alarm up -> "more tension". Presentation copy only.
 */
export function emotionChangeReason(change: EmotionAxisChange): string {
  const REASONS: Record<EmotionAxis, { up: string; down: string }> = {
    Rage: { up: "hotter", down: "cooler" },
    Alarm: { up: "more tension", down: "calmer" },
    Doubt: { up: "looks edited", down: "reads truer" },
    Funny: { up: "lighter", down: "more serious" },
    Pity: { up: "sadder", down: "less pity" },
    Warm: { up: "warmer", down: "colder" },
  };
  return REASONS[change.axis][change.dir];
}

// ── Hybrid soft-backlash (presentation reading; never feeds scoring) ──────────
//
// Two complementary reads per crowd, both 0..1:
//   • crowdFit      — how much of the crowd's GREEN "wants" zone the reel fills.
//   • crowdBacklash — how far the reel reaches into the crowd's RED zone.

/**
 * Level-1 tuning knobs. L1 is intentionally forgiving: it is easy to "hit" a
 * want (low GREEN_HIT_FRAC) and hard to trip backlash (high RED_TRIGGER_FRAC +
 * BACKLASH_MIN). These are difficulty knobs shared across scenarios, not
 * scenario data, so they stay in the engine.
 */
export const EMOTION_TUNING = {
  /** A green axis counts as "hit" once the reel fills this fraction of it. */
  GREEN_HIT_FRAC: 0.35,
  /** A red axis only "spills" once the reel fills this fraction of it. */
  RED_TRIGGER_FRAC: 0.8,
  /** Overall red-fill needed before we call a crowd's reaction backlash. */
  BACKLASH_MIN: 0.5,
} as const;

const sum = (ns: number[]) => ns.reduce((a, b) => a + b, 0);

/** 0..1 — fraction of the crowd's GREEN "wants" zone the reel fills. */
export function crowdFit(vector: EmotionVector, crowd: CrowdEmotionProfile): number {
  const greenAxes = EMOTION_AXES.filter((a) => crowd.green[a] > 0);
  const denom = sum(greenAxes.map((a) => crowd.green[a]));
  if (denom <= 0) return 0;
  const covered = sum(greenAxes.map((a) => Math.min(clamp01(vector[a]), crowd.green[a])));
  return clamp01(covered / denom);
}

/** 0..1 — fraction of the crowd's RED backlash zone the reel reaches into. */
export function crowdBacklash(vector: EmotionVector, crowd: CrowdEmotionProfile): number {
  const redAxes = EMOTION_AXES.filter((a) => crowd.red[a] > 0);
  const denom = sum(redAxes.map((a) => crowd.red[a]));
  if (denom <= 0) return 0;
  const covered = sum(redAxes.map((a) => Math.min(clamp01(vector[a]), crowd.red[a])));
  return clamp01(covered / denom);
}

export interface CrowdReaction {
  id: string;
  label: string;
  /** GREEN wants zone fill, 0..1. */
  fit: number;
  /** RED backlash zone fill, 0..1. */
  backlash: number;
  /** Green axes the reel has "hit" (>= GREEN_HIT_FRAC of the want). */
  wantsHit: number;
  /** Total green axes the crowd wants. */
  wantsTotal: number;
  /** True only when the reel has genuinely tipped this crowd into their red. */
  inRed: boolean;
  /** Status face for the compact reaction row. */
  face: string;
  /** Short plain-words status, e.g. "eating it up" / "fine" / "turning on this". */
  status: string;
}

/**
 * Full presentation read for one crowd against the current reel. `served` picks
 * warmer copy for the crowd you're cutting for; other crowds read as bystanders
 * who may or may not be turning on the edit.
 */
export function crowdReaction(
  vector: EmotionVector,
  crowd: CrowdEmotionProfile,
  served = false,
): CrowdReaction {
  const { GREEN_HIT_FRAC, RED_TRIGGER_FRAC, BACKLASH_MIN } = EMOTION_TUNING;
  const greenAxes = EMOTION_AXES.filter((a) => crowd.green[a] > 0);
  const redAxes = EMOTION_AXES.filter((a) => crowd.red[a] > 0);
  const fit = crowdFit(vector, crowd);
  const backlash = crowdBacklash(vector, crowd);
  const wantsTotal = greenAxes.length;
  const wantsHit = greenAxes.filter(
    (a) => clamp01(vector[a]) >= GREEN_HIT_FRAC * crowd.green[a],
  ).length;
  const spilledAxes = redAxes.filter(
    (a) => clamp01(vector[a]) >= RED_TRIGGER_FRAC * crowd.red[a],
  ).length;
  const inRed = backlash >= BACKLASH_MIN && spilledAxes >= 1;

  let face: string;
  let status: string;
  if (inRed) {
    face = "😱";
    status = served ? "backlash brewing" : "turning on this";
  } else if (served) {
    face = fit >= 0.5 ? "🤩" : fit > 0.15 ? "🙂" : "😐";
    status = fit >= 0.5 ? "eating it up" : fit > 0.15 ? "warming up" : "not landing yet";
  } else {
    face = "🙂";
    status = "fine";
  }
  return { id: crowd.id, label: crowd.label, fit, backlash, wantsHit, wantsTotal, inRed, face, status };
}

/**
 * Reactions for all of a scenario's crowds against the current reel, served crowd
 * first (per `config.crowdOrder`). Convenience wrapper the editor panel + Compare
 * framing both read from. Presentation only; never feeds scoring.
 */
export function crowdReactions(vector: EmotionVector, config: LandingConfig): CrowdReaction[] {
  return config.crowdOrder.map((id) =>
    crowdReaction(vector, config.crowds[id]!, id === config.servedCrowdId),
  );
}
